import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useFontScale } from '../hooks/useFontScale';

/**
 * FontScalePicker, small icon-button that opens a dropdown with size options.
 *
 * The dropdown renders via React Portal into <body> so it escapes any parent
 * with overflow: hidden (workspace-page) or stacking-context traps. Position
 * is calculated from the button's bounding rect and updated on scroll/resize.
 */
export default function FontScalePicker({ className = '', buttonStyle = {} }) {
  const { scale, setScale, options } = useFontScale();
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, right: 0 });
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  // Recompute menu position based on button location (right-aligned under button).
  const updateCoords = () => {
    const btn = buttonRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    setCoords({
      top: r.bottom + 6,
      right: window.innerWidth - r.right,
    });
  };

  useLayoutEffect(() => {
    if (!open) return;
    updateCoords();
    const onScroll = () => updateCoords();
    const onResize = () => updateCoords();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  // Close on outside click (must check BOTH the button container and the
  // portal'd menu since they live in different DOM subtrees).
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      const inButton = containerRef.current && containerRef.current.contains(e.target);
      const inMenu = menuRef.current && menuRef.current.contains(e.target);
      if (!inButton && !inMenu) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <div ref={containerRef} className={`font-scale-picker ${className}`}>
      <button
        ref={buttonRef}
        type="button"
        className="font-scale-picker__btn"
        onClick={() => setOpen((v) => !v)}
        aria-label="Tekstgrootte aanpassen"
        aria-haspopup="menu"
        aria-expanded={open}
        title="Tekstgrootte"
        style={buttonStyle}
      >
        <span className="material-icons" aria-hidden="true">format_size</span>
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          role="menu"
          className="font-scale-picker__menu"
          style={{ top: coords.top, right: coords.right }}
        >
          <div className="font-scale-picker__heading">Tekstgrootte</div>
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              role="menuitemradio"
              aria-checked={scale === opt.value}
              className={`font-scale-picker__option ${scale === opt.value ? 'font-scale-picker__option--active' : ''}`}
              onClick={() => {
                setScale(opt.value);
                setOpen(false);
              }}
            >
              <span style={{ fontSize: `${0.85 * opt.value}rem` }}>{opt.label}</span>
              {scale === opt.value && <span aria-hidden="true" style={{ marginLeft: 8 }}>✓</span>}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}
